/**
 * Portal Manta · MatrizDisciplinas
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { V, S, fmtNum, sumBy, sigKey, fmtBRL, AmpliBadge, OBRAS_ROD } from '../../globals.js';
import { THl, TH, TDl, TD } from '../../app-globals.js';

function MatrizDisciplinas({lote}){
  const sig = sigKey(lote);
  const cf = CONSOL_FIN[sig]||{};
  const od = OBRAS_ROD[sig]||{};
  const sistema = od.sistema||[];
  const extLote = sistema.reduce((s,r)=>s+(r.ext||0),0)||1;
  const totSicro = Object.values(cf).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const [metric,setMetric] = useState('valor'); // valor | qtd | rkm
  const cores = {'PAVIMENTACAO':V.or,'TERRAPLENOS E CONTENCOES':V.bl,'DRENAGEM':V.gn,
                 'OAES':V.rxn||'#7c3aed','SINALIZACAO':V.am||'#d97706',
                 'ILUMINACAO':V.ind||'#0891b2','SERVICOS PRELIMINARES':V.gr||'#475569'};
  const discKeys = Object.keys(cf);
  // Calcular matriz: para cada (rodovia × disciplina) → valor rateado, qtd dominante, R$/km
  const matriz = useMemo(()=>{
    const m = {};
    sistema.forEach(r=>{
      const fr = extLote?r.ext/extLote:0;
      m[r.rod] = {};
      discKeys.forEach(d=>{
        const items = cf[d]||[];
        const valor = sumBy(items,'v')*fr;
        const qty = sumBy(items,'q')*fr;
        const rkm = r.ext?valor/r.ext:0;
        m[r.rod][d] = {valor, qty, rkm};
      });
    });
    return m;
  },[sig]);
  // Para o heatmap: precisar do max por coluna (disciplina) para normalizar
  const maxPorDisc = {};
  discKeys.forEach(d=>{
    maxPorDisc[d] = Math.max(...sistema.map(r=>matriz[r.rod][d][metric]||0),1);
  });
  // Cor de intensidade — gradiente do branco para cor da disciplina
  const corCelula = (cor, intensity)=>{
    if(intensity<=0) return '#fff';
    // converte hex → rgb e mistura com branco
    const hex = cor.replace('#','');
    const r=parseInt(hex.slice(0,2),16),g=parseInt(hex.slice(2,4),16),b=parseInt(hex.slice(4,6),16);
    const t = Math.min(0.85, intensity*0.85);
    const mix = (c)=>Math.round(255 + (c-255)*t);
    return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
  };
  const fmtCelula = (v, m)=>{
    if(!v) return '—';
    if(m==='valor') return fmtBRL(v,0);
    if(m==='qtd')   return fmtNum(v,0);
    if(m==='rkm')   return fmtBRL(v,0)+'/km';
    return v;
  };
  return(<div style={S.page}>
    <div style={S.h1}>Matriz Disciplinas × Rodovias <AmpliBadge/></div>
    <div style={S.sub}>
      Heatmap visual de obras de ampliação · {sistema.length} rodovias × {discKeys.length} disciplinas · Σ <b>{fmtBRL(totSicro)}</b>
    </div>
    {/* Toggle de métrica */}
    <div style={{display:'flex',gap:6,marginBottom:14}}>
      {[['valor','R$ rateado'],['qtd','Quantidade dominante'],['rkm','R$/km']].map(([k,l])=>(
        <button key={k} onClick={()=>setMetric(k)} style={{
          padding:'6px 14px',fontSize:11,fontWeight:600,
          background:metric===k?V.bl:'#fff',
          color:metric===k?'#fff':V.gr,
          border:'1px solid '+(metric===k?V.bl:V.b1),
          borderRadius:4,cursor:'pointer'
        }}>{l}</button>
      ))}
    </div>
    {/* Matriz heatmap */}
    <div style={{...S.card,overflow:'auto'}}>
      <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
        <thead style={S.thRow}>
          <tr style={{borderBottom:'2px solid '+V.b1}}>
            <th style={{...THl,minWidth:140,position:'sticky',left:0,background:V.g0,zIndex:2}}>Rodovia</th>
            <th style={TH}>Ext (km)</th>
            {discKeys.map(d=>(
              <th key={d} style={{...TH,minWidth:100,background:cores[d]+'22',color:cores[d]}}>
                {d.replace('TERRAPLENOS E CONTENCOES','TERRAPLENOS').replace('SERVICOS PRELIMINARES','SERV.PREL.').replace('PAVIMENTACAO','PAVIMENT.').replace('SINALIZACAO','SINALIZ.').replace('ILUMINACAO','ILUMIN.')}
              </th>
            ))}
            <th style={{...TH,fontWeight:700}}>TOTAL ROD</th>
          </tr>
        </thead>
        <tbody>
          {sistema.map(r=>{
            const fr = extLote?r.ext/extLote:0;
            const totRod = totSicro*fr;
            return(<tr key={r.rod} style={S.trLine}>
              <td style={{...TDl,fontWeight:700,color:V.br,position:'sticky',left:0,background:'#fff',borderRight:'1px solid '+V.b1}}>{r.rod}</td>
              <td style={TD}>{fmtNum(r.ext,1)}</td>
              {discKeys.map(d=>{
                const cel = matriz[r.rod][d];
                const v = cel[metric]||0;
                const intensity = maxPorDisc[d]?v/maxPorDisc[d]:0;
                const bg = corCelula(cores[d]||V.bl, intensity);
                return(<td key={d} style={{
                  ...TD,padding:'8px 6px',background:bg,
                  textAlign:'center',fontFamily:'monospace',
                  color:intensity>0.5?'#fff':V.br,fontWeight:intensity>0.3?700:500
                }}>{fmtCelula(v,metric)}</td>);
              })}
              <td style={{...TD,fontWeight:700,color:V.br}}>{metric==='valor'?fmtBRL(totRod):metric==='rkm'?fmtBRL(r.ext?totRod/r.ext:0,0)+'/km':'—'}</td>
            </tr>);
          })}
          <tr style={S.tfootRow}>
            <td colSpan={2} style={{padding:'8px 10px',fontSize:11,fontWeight:700,position:'sticky',left:0,background:V.g0,borderRight:'1px solid '+V.b1}}>TOTAL DISC.</td>
            {discKeys.map(d=>(<td key={d} style={{padding:'8px 6px',fontSize:10,fontWeight:700,textAlign:'center',background:cores[d]+'33',color:V.br}}>
              {metric==='valor'?fmtBRL(sumBy(cf[d]||[],'v')):metric==='qtd'?fmtNum(sumBy(cf[d]||[],'q'),0):'—'}
            </td>))}
            <td style={{padding:'8px 6px',fontSize:11,fontWeight:700,textAlign:'right',color:V.br,background:V.g0}}>{metric==='valor'?fmtBRL(totSicro):'—'}</td>
          </tr>
        </tbody>
      </table>
    </div>
    {/* Legenda */}
    <div style={{...S.cardP,marginTop:10,fontSize:10,color:V.gr}}>
      <b>Como ler:</b> intensidade da cor = magnitude relativa dentro da disciplina (coluna). Branco = sem participação. Sólido = maior valor.
      Critério de rateio: proporcional à extensão da rodovia.
    </div>
  </div>);
}

export { MatrizDisciplinas };
export default MatrizDisciplinas;
